import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Download } from "lucide-react";
import { SITE } from "@/lib/site";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Products" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 transition-all ${scrolled ? "backdrop-blur-xl bg-white/85 border-b border-slate-200/70 shadow-sm" : "bg-white/60 backdrop-blur"}`}
    >
      <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-8 flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2 md:gap-3">
          <img src={SITE.logo} alt="Green Fuse Energy" className="h-9 md:h-11 w-auto" />
          <div className="hidden sm:block leading-tight">
            <div className="font-display font-extrabold text-[15px] md:text-base text-slate-900">Green Fuse Energy</div>
            <div className="text-[10px] md:text-[11px] uppercase tracking-widest text-slate-500">Industrial Solutions</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-tight transition-colors ${isActive ? "text-slate-900" : "text-slate-600 hover:text-slate-900"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={SITE.catalogue}
            target="_blank"
            rel="noreferrer"
            data-testid="header-download-catalogue"
            className="btn-ghost inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
          >
            <Download className="w-4 h-4" /> Catalogue
          </a>
          <Link
            to="/quote"
            data-testid="header-request-quote"
            className="btn-orange inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold"
          >
            Request a Quote
          </Link>
        </div>

        <button
          className="md:hidden p-2 rounded-lg border border-slate-200"
          onClick={() => setOpen(!open)}
          data-testid="header-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
          <div className="px-4 py-4 flex flex-col gap-3">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`mnav-${n.label.toLowerCase()}`}
                className={({ isActive }) => `py-2 text-base ${isActive ? "text-slate-900 font-semibold" : "text-slate-600"}`}
              >
                {n.label}
              </NavLink>
            ))}
            <a href={SITE.catalogue} target="_blank" rel="noreferrer" className="btn-ghost inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium justify-center">
              <Download className="w-4 h-4" /> Download Catalogue
            </a>
            <Link to="/quote" className="btn-orange inline-flex items-center justify-center px-4 py-2.5 rounded-full text-sm font-semibold">Request a Quote</Link>
          </div>
        </div>
      )}
    </header>
  );
}
